/* eslint-disable */
import {
	ChevronDown,
	ChevronUp,
	CirclePlus,
	CookingPot,
	Edit2,
	FolderSync,
	XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Table } from 'rsuite';
const { Column, ColumnGroup, Cell, HeaderCell } = Table;
import GenerateRow from './GenerateRow';
import { useAuth } from '../Auth/Login/auth';
import './poTable.css';

// Clients whose order-mode store columns sort purely priority-wise,
// without the not-punched-first grouping.
const PRIORITY_ONLY_SORT_CUSTOMER_IDS = [68];

const PoTableMultiple = (props) => {
	const {
		tableData,
		renderCellData,
		fixedColumns = [],
		columnWidth = null,
		showGenerateRow = false,
		onGeneratePlan = () => { },
		generatedPlans = [],
		virtulization = false,
		showHeader = false,
		isUnsaved = false,
		emptyMessage = 'No data available',
		footerProps = {},
		footerPlacement = 'bottom',
		showGenerateRowPlacement = 'bottom',
		generateRows = [],
		sortColumn = null,
		sortType = null,
		handleSortColumn = () => { },
		showGroupedHeaders = false,
		renderHeaderContent = null,
		headerHeight = null,
		mode = '',
		fitParent = false,
	} = props;

	const { appConfig = {} } = useAuth();
	const customerId = appConfig?.customerId;
	const containerRef = useRef(null);
	const topSectionRef = useRef(null);
	const bottomSectionRef = useRef(null);
	const [tableHeight, setTableHeight] = useState(400);
	const [scrollLeft, setScrollLeft] = useState(0);
	const [scrollableWidth, setScrollableWidth] = useState(0);
	const [isGenerateRowsCollapsed, setIsGenerateRowsCollapsed] =
		useState(false);

	// 1. Sort Headers (Fixed -> Stores Sorted -> Trailing)
	const sortedHeaders = useMemo(() => {
		if (!tableData?.headers) return [];
		const isOrderMode = mode === 'order';
		const fixedHeaderIds = ['S.No', 'Item Name', 'SkuCode'];
		const trailingIds = ['Total', 'Actions'];

		const fixed = tableData.headers.filter((h) =>
			fixedHeaderIds.includes(h.id) || h.fixed || fixedColumns.includes(h.name) || fixedColumns.includes(h.id)
		);
		const trailing = tableData.headers.filter((h) =>
			trailingIds.includes(h.id)
		);
		const sortable = tableData.headers.filter(
			(h) => !fixed.some(f => f.id === h.id) && !trailing.some(t => t.id === h.id)
		);
		if (isOrderMode) {
			const priorityOnly = PRIORITY_ONLY_SORT_CUSTOMER_IDS.includes(Number(customerId));
			sortable.sort((a, b) => {
				if (!priorityOnly) {
					const notPunchedA = a.notPunched ? 0 : 1;
					const notPunchedB = b.notPunched ? 0 : 1;
					if (notPunchedA !== notPunchedB) {
						return notPunchedA - notPunchedB;
					}
				}

				const priorityA = a.priority ?? Infinity;
				const priorityB = b.priority ?? Infinity;

				if (priorityA !== priorityB) {
					return priorityA - priorityB;
				}
				return (a.name || '').localeCompare(b.name || '');
			});
		}

		return [...fixed, ...sortable, ...trailing];
	}, [tableData?.headers, fixedColumns, mode, customerId]);

	// 2. Sort Rows (Apply default 'Item Name' sort if not specified and mode is 'order')
	const sortedData = useMemo(() => {
		const isOrderMode = mode === 'order';
		const effectiveSortCol = sortColumn || (isOrderMode ? 'Item Name' : null);
		const effectiveSortType = sortType || (isOrderMode ? 'asc' : null);

		if (!effectiveSortCol || !effectiveSortType) {
			return tableData?.rows || [];
		}

		const rows = [...(tableData?.rows || [])].sort((a, b) => {
			let x = a[effectiveSortCol];
			let y = b[effectiveSortCol];

			// Handle cell objects (e.g. {qty, unit})
			if (x && typeof x === 'object' && 'qty' in x) x = x.qty;
			if (y && typeof y === 'object' && 'qty' in y) y = y.qty;

			// Handle null/undefined values (push to end)
			if (x === null || x === undefined || x === '') return 1;
			if (y === null || y === undefined || y === '') return -1;

			const isXNumeric =
				typeof x === 'number' ||
				(typeof x === 'string' &&
					!isNaN(parseFloat(x)) &&
					x.match(/^[0-9.]+.*$/));
			const isYNumeric =
				typeof y === 'number' ||
				(typeof y === 'string' &&
					!isNaN(parseFloat(y)) &&
					y.match(/^[0-9.]+.*$/));

			if (isXNumeric && isYNumeric) {
				x = parseFloat(x);
				y = parseFloat(y);
			} else {
				x = x?.toString()?.toLowerCase() || '';
				y = y?.toString()?.toLowerCase() || '';
			}

			if (effectiveSortType === 'asc') {
				return x > y ? 1 : -1;
			} else {
				return x < y ? 1 : -1;
			}
		});
		return rows;
	}, [tableData?.rows, sortColumn, sortType, mode]);

	const totalGenerateRows = (showGenerateRow ? 1 : 0) + generateRows.length;

	// Separate fixed and scrollable columns based on header property or fixedColumns prop
	const fixedHeaders = sortedHeaders.filter(
		(header) =>
			header.fixed ||
			fixedColumns.includes(header.name) ||
			fixedColumns.includes(header.id) ||
			['S.No', 'Item Name', 'SkuCode'].includes(header.id)
	);
	const scrollableHeaders = sortedHeaders.filter(
		(header) =>
			!fixedHeaders.some(f => f.id === header.id)
	);

	useEffect(() => {
		const updateHeight = () => {
			if (!containerRef.current) return;

			// Measure the actual rendered heights of the row sections
			// (estimating them causes the bottom rows to get clipped by
			// the modal's overflow-hidden when rows are taller than the
			// estimate)
			const topSectionHeight = topSectionRef.current?.offsetHeight || 0;
			const bottomSectionHeight =
				bottomSectionRef.current?.offsetHeight || 0;
			const footerHeight = topSectionHeight + bottomSectionHeight;

			let usableHeight;
			if (fitParent) {
				// Parent constrains our height via flexbox — the table gets
				// exactly what remains after the row sections.
				usableHeight =
					containerRef.current.clientHeight - footerHeight;
			} else {
				const containerTop =
					containerRef.current.getBoundingClientRect().top;
				usableHeight =
					window.innerHeight - containerTop - 100 - footerHeight;
			}

			setTableHeight(usableHeight > 200 ? usableHeight : 200);
		};

		// Initial calculation with a small delay to ensure modal is fully rendered
		const timeoutId = setTimeout(updateHeight, 100);

		// Also update on resize
		window.addEventListener('resize', updateHeight);

		// Re-measure when the container or the row sections change size
		// (rows load async, collapse/expand animates its max-height over
		// 250ms, header content above can grow after data loads)
		const observer = new ResizeObserver(updateHeight);
		if (containerRef.current) observer.observe(containerRef.current);
		if (topSectionRef.current) observer.observe(topSectionRef.current);
		if (bottomSectionRef.current) observer.observe(bottomSectionRef.current);

		return () => {
			clearTimeout(timeoutId);
			window.removeEventListener('resize', updateHeight);
			observer.disconnect();
		};
	}, [
		fitParent,
		tableData?.rows?.length,
		showGenerateRow,
		isGenerateRowsCollapsed,
		totalGenerateRows,
	]);

	useEffect(() => {
		const updateScrollableWidth = () => {
			if (containerRef.current) {
				const tableElement =
					containerRef.current.querySelector('.rs-table');
				if (tableElement) {
					const fixedWidth = fixedHeaders.reduce(
						(acc, header) =>
							acc +
							(header.flexGrow
								? header.minWidth || 120
								: header.width || 120),
						0
					);
					const tableWidth = tableElement.offsetWidth;
					setScrollableWidth(tableWidth - fixedWidth);
				}
			}
		};

		const timeoutId = setTimeout(updateScrollableWidth, 150);
		window.addEventListener('resize', updateScrollableWidth);

		return () => {
			clearTimeout(timeoutId);
			window.removeEventListener('resize', updateScrollableWidth);
		};
	}, [tableData?.headers?.length]);

	// Handle table scroll to sync with generate row.
	// Scroll fires ~60x/sec; calling setScrollLeft on every event re-renders the
	// whole table (and every visible cell) each time. Coalesce into one state
	// update per animation frame and skip if the value hasn't changed.
	const latestScrollLeftRef = useRef(0);
	const scrollRafRef = useRef(null);
	const handleScroll = (scrollX) => {
		latestScrollLeftRef.current = scrollX;
		if (scrollRafRef.current != null) return;
		scrollRafRef.current = requestAnimationFrame(() => {
			scrollRafRef.current = null;
			setScrollLeft((prev) =>
				prev === latestScrollLeftRef.current
					? prev
					: latestScrollLeftRef.current
			);
		});
	};

	useEffect(() => {
		return () => {
			if (scrollRafRef.current != null) {
				cancelAnimationFrame(scrollRafRef.current);
			}
		};
	}, []);

	if (
		!showHeader &&
		(!tableData?.rows?.length || !tableData?.headers?.length)
	) {
		return <div>No data available</div>;
	}

	// Shared props for GenerateRow instances
	const sharedRowProps = {
		fixedHeaders,
		scrollableHeaders,
		columnWidth,
		scrollLeft,
		scrollableWidth,
		isUnsaved,
	};

	const generateRowsContent = (
		<>
			{generateRows.length > 0 &&
				generateRows.map((row, index) => (
					<GenerateRow
						key={`generate-row-${index}`}
						{...sharedRowProps}
						{...row}
					/>
				))}
		</>
	);

	const showGenerateRowContent = showGenerateRow && (
		<GenerateRow
			{...sharedRowProps}
			onGeneratePlan={onGeneratePlan}
			generatedPlans={generatedPlans}
			{...footerProps}
		/>
	);

	const allRowConfigs = [
		...(showGenerateRow
			? [{ label: 'Production Plan', ...footerProps }]
			: []),
		...generateRows,
	];

	const generateRowsJSX =
		generateRows.length > 0 ? (
			<div className='flex flex-col'>
				{/* Collapse/Expand toggle - shown when 2+ rows */}
				{totalGenerateRows >= 2 && (
					<button
						onClick={() =>
							setIsGenerateRowsCollapsed((prev) => !prev)
						}
						className='flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors border-t border-gray-200 bg-gray-50 w-full'
					>
						{isGenerateRowsCollapsed ? (
							<ChevronDown size={14} />
						) : (
							<ChevronUp size={14} />
						)}
						{isGenerateRowsCollapsed && (
							<div className='flex items-center gap-2 flex-wrap'>
								{allRowConfigs.map((row, i) => (
									<span
										key={i}
										className='flex items-center gap-1 text-[11px] text-gray-600'
									>
										{row.icon &&
											(typeof row.icon ===
												'string' ? null : (
												<span className='opacity-70'>
													{row.icon}
												</span>
											))}
										<span className='font-semibold'>
											{row.label}:
										</span>
										<span>
											{row.collapsedSummary || '—'}
										</span>
										{i < allRowConfigs.length - 1 && (
											<span className='text-gray-300 ml-1'>
												|
											</span>
										)}
									</span>
								))}
							</div>
						)}
					</button>
				)}
				{/* Rows with smooth transition */}
				<div
					style={{
						maxHeight: isGenerateRowsCollapsed ? '0px' : '500px',
						overflow: 'hidden',
						transition: 'max-height 0.25s ease-in-out',
					}}
				>
					{generateRowsContent}
				</div>
			</div>
		) : null;

	const showGenerateRowJSX = showGenerateRowContent ? (
		<div className='flex flex-col'>{showGenerateRowContent}</div>
	) : null;

	return (
		<div className='flex-1 min-h-0 flex flex-col' ref={containerRef}>
			{/* Rows at top if placement is 'top' */}
			<div ref={topSectionRef} className='shrink-0'>
				{footerPlacement === 'top' && generateRowsJSX}
				{showGenerateRowPlacement === 'top' && showGenerateRowJSX}
			</div>

			<Table
				data={sortedData}
				height={tableHeight}
				headerHeight={
					headerHeight ? headerHeight : showGroupedHeaders ? 80 : 60
				}
				rowHeight={(rowData) => {
					// Only the repeat/custom frequency editor grows the row. Invalid
					// quantities are shown via an inline alert icon (no height change),
					// so hasQtyError intentionally does NOT change the row height.
					return rowData?.increaseHeight ? 140 : 60;
				}}
				renderEmpty={() => (
					<div className='flex flex-col items-center justify-center h-full gap-3 text-gray-400'>
						<FolderSync size={48} className='opacity-20' />
						<p className='text-sm font-medium'>{emptyMessage}</p>
					</div>
				)}
				className='rsuite-table'
				onScroll={handleScroll}
				virtualized={virtulization}
				sortColumn={sortColumn}
				sortType={sortType}
				onSortColumn={handleSortColumn}
			>
				{/* Fixed Columns */}
				{fixedHeaders.map((header) => (
					<Column
						key={header?.id}
						{...(header?.flexGrow
							? {
								flexGrow: header.flexGrow,
								minWidth: header?.minWidth || 120,
							}
							: { width: header?.width || 120 })}
						fixed
						align='center'
						sortable={header?.sortable}
					>
						<HeaderCell
							className='text-center bg-amber-100'
							style={{
								fontSize: '12px',
								fontWeight: '500',
								background: '#fef3c7',
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								height: '100%',
							}}
						>
							{header?.name}
						</HeaderCell>
						<Cell
							dataKey={header?.id}
							className='text-center text-wrap'
							style={{
								fontSize: '12px',
							}}
						>
							{(rowData, rowIndex) =>
								renderCellData(rowData, rowIndex, header)
							}
						</Cell>
					</Column>
				))}

				{/* Scrollable Columns */}
				{(() => {
					if (!showGroupedHeaders) {
						return scrollableHeaders.map((header) => (
							<Column
								key={header?.id}
								flexGrow={1}
								minWidth={
									columnWidth
										? columnWidth
										: header?.width || 100
								}
								align='center'
								sortable={header?.sortable}
							>
								<HeaderCell
									className='text-center text-wrap break-words bg-yellow-100'
									style={{
										fontSize: '12px',
										fontWeight: '500',
										backgroundColor: '#fef3c7',
										borderRight:
											header.isWeekEnd &&
											'2px solid #000',
										display: 'flex',
										flexDirection: header?.extraInfo
											? 'column'
											: 'row',
										justifyContent: 'center',
										alignItems: 'center',
										padding: '4px 6px',
										whiteSpace: 'normal',
										wordBreak: 'break-word',
										gap: '4px',
										height: '100%',
									}}
								>
									<div className='flex flex-col gap-1 items-center justify-center'>
										{header?.extraInfo && (
											<div className='text-amber-600 font-bold'>
												{header?.extraInfo}
											</div>
										)}
										<div className='w-full text-center leading-tight'>
											{header?.name}
										</div>
										{renderHeaderContent &&
											renderHeaderContent(header)}
									</div>
								</HeaderCell>
								<Cell
									dataKey={header?.id}
									align='center'
									className={
										header.highlightColumn
											? 'bg-blue-50'
											: ''
									}
									style={{
										padding: '8px 4px',
										fontSize: '12px',
										backgroundColor: header.highlightColumn
											? '#eff6ff'
											: 'transparent',
									}}
								>
									{(rowData, rowIndex) =>
										renderCellData(
											rowData,
											rowIndex,
											header
										)
									}
								</Cell>
							</Column>
						));
					}

					// Group headers by groupName
					const groups = [];
					let currentGroup = null;

					scrollableHeaders.forEach((header) => {
						const groupName = header.groupName;
						if (groupName) {
							if (
								currentGroup &&
								currentGroup.name === groupName
							) {
								currentGroup.headers.push(header);
							} else {
								currentGroup = {
									name: groupName,
									headers: [header],
									isGroup: true,
								};
								groups.push(currentGroup);
							}
						} else {
							groups.push({ header: header, isGroup: false });
							currentGroup = null;
						}
					});
					return groups.map((group, idx) => {
						if (group.isGroup) {
							return (
								<ColumnGroup
									key={`group-${idx}`}
									header={
										<div
											title={group.name}
											style={{
												textAlign: 'center',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												color: '#92400e',
												fontSize: '12px',
												fontWeight: 'bold',
												backgroundColor: '#fef3c7',
												width: '100%',
												height: '100%',
												whiteSpace: 'normal',
												wordBreak: 'break-word',
												gap: '4px',
											}}
										>
											{group.name &&
												group.name.length > 25 &&
												group.headers.length < 3
												? `${group.name.substring(
													0,
													25
												)}...`
												: group.name}
										</div>
									}
								>
									{group.headers.map((header) => (
										<Column
											key={header?.id}
											flexGrow={1}
											minWidth={
												columnWidth
													? columnWidth
													: header?.width || 100
											}
											align='center'
											sortable={header?.sortable}
										>
											<HeaderCell
												className='text-center text-wrap break-words bg-yellow-100'
												style={{
													fontSize: '11px',
													fontWeight: '500',
													backgroundColor: '#fef3c7',
													borderRight:
														'2px solid black',
													display: '-webkit-box',
													padding: '2px 4px',
													wordBreak: 'break-word',
													color: '#000000',
													lineHeight: '1.2',
													textAlign: 'center',
													alignContent: 'center',
												}}
											>
												<div className='flex flex-col gap-1 items-center justify-center h-full'>
													{header?.extraInfo && (
														<div className='text-amber-600 font-bold'>
															{header?.extraInfo}
														</div>
													)}
													<div className='w-full text-center leading-tight'>
														{header?.name}
													</div>
												</div>
											</HeaderCell>
											<Cell
												dataKey={header?.id}
												align='center'
												className={
													header.highlightColumn
														? 'bg-blue-50'
														: ''
												}
												style={{
													padding: '8px 4px',
													fontSize: '12px',
													backgroundColor:
														header.highlightColumn
															? '#eff6ff'
															: 'transparent',
												}}
											>
												{(rowData, rowIndex) =>
													renderCellData(
														rowData,
														rowIndex,
														header
													)
												}
											</Cell>
										</Column>
									))}
								</ColumnGroup>
							);
						} else {
							const { header } = group;
							return (
								<Column
									key={header?.id}
									flexGrow={1}
									minWidth={
										columnWidth
											? columnWidth
											: header?.width || 100
									}
									align='center'
									sortable={header?.sortable}
								>
									<HeaderCell
										className='text-center text-wrap break-words bg-yellow-100'
										style={{
											fontSize: '12px',
											fontWeight: '500',
											backgroundColor: '#fef3c7',
											borderRight:
												header.isWeekEnd &&
												'2px solid #000',
											display: 'flex',
											flexDirection: header?.extraInfo
												? 'column'
												: 'row',
											justifyContent: 'center',
											alignItems: 'center',
											padding: '4px 6px',
											whiteSpace: 'normal',
											wordBreak: 'break-word',
											gap: '4px',
											height: '100%',
										}}
									>
										<div className='flex flex-col gap-2 items-center justify-center'>
											{header?.extraInfo && (
												<div className='text-amber-600 font-bold'>
													{header?.extraInfo}
												</div>
											)}
											<div className='w-full text-center leading-tight'>
												{header?.name}
											</div>
											{renderHeaderContent &&
												renderHeaderContent(header)}
										</div>
									</HeaderCell>
									<Cell
										dataKey={header?.id}
										align='center'
										className={
											header.highlightColumn
												? 'bg-blue-50'
												: ''
										}
										style={{
											padding: '8px 4px',
											fontSize: '12px',
											backgroundColor:
												header.highlightColumn
													? '#eff6ff'
													: 'transparent',
										}}
									>
										{(rowData, rowIndex) =>
											renderCellData(
												rowData,
												rowIndex,
												header
											)
										}
									</Cell>
								</Column>
							);
						}
					});
				})()}
			</Table>

			{/* Footer rows at bottom if placement is 'bottom' (default) */}
			<div ref={bottomSectionRef} className='shrink-0'>
				{showGenerateRowPlacement === 'bottom' && showGenerateRowJSX}
				{footerPlacement === 'bottom' && generateRowsJSX}
			</div>
		</div>
	);
};

export default PoTableMultiple;
